import React, { useState, useEffect, useRef } from 'react';
import { uploadData } from 'aws-amplify/storage';
import useShopSettings from '../platform/settings/useShopSettings';
import { DEFAULT_SHOP_SETTINGS } from '../platform/settings/defaultShopSettings';
import CatalogTab from './CatalogTab';
import ManualView from './manual/ManualView';
import rekonLogo from '../Assets/Rekon name.png';
import './CustomizeView.css';

const COLOR_FIELDS = [
  { key: 'primaryColor',  label: 'Primary',  hint: 'Header, nav, primary text' },
  { key: 'accentColor',   label: 'Accent',   hint: 'Buttons, highlights, focus' },
  { key: 'successColor',  label: 'Success',  hint: 'Healthy, complete status' },
  { key: 'dangerColor',   label: 'Danger',   hint: 'Critical alerts, hard-down' },
];

const TIMEZONE_OPTIONS = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
];

// ─── Business Tab ─────────────────────────────────────────────────────────────

const QB_ITEM_FIELDS = [
  { key: 'labor',        label: 'Labor',        hint: 'Product/Service every labor line posts to' },
  { key: 'parts',        label: 'Parts',        hint: 'Product/Service every part line posts to' },
  { key: 'shopSupplies', label: 'Shop Supplies', hint: '' },
  { key: 'tax',          label: 'Sales Tax',    hint: '' },
];

const QB_ACCOUNT_FIELDS = [
  { key: 'receivable',   label: 'Accounts Receivable' },
  { key: 'labor',        label: 'Labor Income' },
  { key: 'parts',        label: 'Parts Income' },
  { key: 'shopSupplies', label: 'Shop Supplies Income' },
  { key: 'tax',          label: 'Sales Tax Payable' },
];

function BillingTab({ draft, updatePricing, updateAccounting }) {
  const p = draft.pricing || {};
  const a = draft.accounting || DEFAULT_SHOP_SETTINGS.accounting;
  const items = { ...DEFAULT_SHOP_SETTINGS.accounting.items, ...(a.items || {}) };
  const accounts = { ...DEFAULT_SHOP_SETTINGS.accounting.accounts, ...(a.accounts || {}) };
  const isDesktop = a.quickbooksFormat === 'iif';
  return (
    <>
      <div className="cv-field">
        <label className="cv-label">Default Labor Rate ($/hr)</label>
        <input
          className="tt-input"
          type="number" min="0" step="1"
          value={p.defaultLaborRate ?? ''}
          onChange={e => updatePricing('defaultLaborRate', parseFloat(e.target.value) || 0)}
        />
        <span className="cv-field-hint">Pre-fills new labor lines on estimates and invoices</span>
      </div>

      <div className="cv-field">
        <label className="cv-label">Sales Tax Rate (%)</label>
        <input
          className="tt-input"
          type="number" min="0" max="100" step="0.01"
          value={p.taxRate ?? ''}
          onChange={e => updatePricing('taxRate', parseFloat(e.target.value) || 0)}
        />
        <span className="cv-field-hint">Applied to new estimates; adjustable per estimate</span>
      </div>

      <div className="cv-field">
        <label className="cv-label">Payment Terms (days)</label>
        <input
          className="tt-input"
          type="number" min="0" step="1"
          value={p.paymentTermsDays ?? ''}
          onChange={e => updatePricing('paymentTermsDays', parseInt(e.target.value, 10) || 0)}
        />
        <span className="cv-field-hint">Net terms on invoices — drives Collections aging</span>
      </div>

      <div className="cv-field">
        <label className="cv-label">Invoice / Estimate Disclaimer</label>
        <textarea
          className="tt-textarea"
          rows={5}
          value={p.invoiceDisclaimer ?? ''}
          onChange={e => updatePricing('invoiceDisclaimer', e.target.value)}
          placeholder="All sales final. Not responsible for loss or damage…"
        />
        <span className="cv-field-hint">Printed as small print at the bottom of every estimate and invoice</span>
      </div>

      <h3 className="cv-subhead">QuickBooks Export</h3>

      <div className="cv-field">
        <label className="cv-label">Which QuickBooks do you run?</label>
        <select
          className="tt-input"
          value={a.quickbooksFormat || 'csv'}
          onChange={e => updateAccounting('quickbooksFormat', e.target.value)}
        >
          <option value="csv">QuickBooks Online — CSV import</option>
          <option value="iif">QuickBooks Desktop / Enterprise — IIF import</option>
        </select>
        <span className="cv-field-hint">
          {isDesktop
            ? 'Invoices export as .iif — File → Utilities → Import → IIF Files'
            : 'Invoices export as .csv — Gear → Import Data → Invoices'}
        </span>
      </div>

      {!isDesktop && (
        <div className="cv-field">
          <label className="cv-label">Sales Tax on Import</label>
          <select
            className="tt-input"
            value={a.qboTaxMode || 'quickbooks'}
            onChange={e => updateAccounting('qboTaxMode', e.target.value)}
          >
            <option value="quickbooks">Let QuickBooks calculate it (Automated Sales Tax)</option>
            <option value="lineitem">Send tax as its own line item</option>
          </select>
          <span className="cv-field-hint">
            {a.qboTaxMode === 'lineitem'
              ? 'Only correct if your QuickBooks company is NOT on Automated Sales Tax — otherwise the invoice gets taxed twice.'
              : 'Lines export coded TAX / NON and QuickBooks does the math. Right for almost every US company.'}
          </span>
        </div>
      )}

      <div className="cv-field">
        <label className="cv-label">Product / Service Names</label>
        <span className="cv-field-hint">
          Must match the Products and Services already set up in QuickBooks, or the import will
          create duplicates.
        </span>
        {QB_ITEM_FIELDS.map(f => (
          <div key={f.key} className="cv-subfield">
            <label className="cv-sublabel">{f.label}</label>
            <input
              className="tt-input"
              value={items[f.key] ?? ''}
              onChange={e => updateAccounting(f.key, e.target.value, 'items')}
              maxLength={100}
            />
          </div>
        ))}
      </div>

      {isDesktop && (
        <div className="cv-field">
          <label className="cv-label">Chart of Accounts (Desktop only)</label>
          <span className="cv-field-hint">
            IIF posts to accounts by name — these must match your QuickBooks Desktop chart of
            accounts exactly.
          </span>
          {QB_ACCOUNT_FIELDS.map(f => (
            <div key={f.key} className="cv-subfield">
              <label className="cv-sublabel">{f.label}</label>
              <input
                className="tt-input"
                value={accounts[f.key] ?? ''}
                onChange={e => updateAccounting(f.key, e.target.value, 'accounts')}
                maxLength={100}
              />
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function BusinessTab({ draft, updateBusiness }) {
  return (
    <>
      <div className="cv-field">
        <label className="cv-label">Company Name</label>
        <input
          className="tt-input"
          value={draft.business.companyName}
          onChange={e => updateBusiness('companyName', e.target.value)}
          placeholder="REKON"
          maxLength={60}
        />
        <span className="cv-field-hint">Used for ownership, reports, and future customer-facing views</span>
      </div>

      <div className="cv-field">
        <label className="cv-label">Shop / Location Name</label>
        <input
          className="tt-input"
          value={draft.business.shopName}
          onChange={e => updateBusiness('shopName', e.target.value)}
          placeholder="Albany"
          maxLength={60}
        />
      </div>

      <div className="cv-field">
        <label className="cv-label">Shop ID</label>
        <input
          className="tt-input"
          value={draft.business.shopId}
          onChange={e => updateBusiness('shopId', e.target.value)}
          placeholder="DGP-ALBANY"
          maxLength={40}
        />
        <span className="cv-field-hint">Kept stable for future multi-shop data separation</span>
      </div>

      <div className="cv-field">
        <label className="cv-label">Time Zone</label>
        <select
          className="tt-input"
          value={draft.business.timezone}
          onChange={e => updateBusiness('timezone', e.target.value)}
        >
          {TIMEZONE_OPTIONS.map(tz => <option key={tz} value={tz}>{tz}</option>)}
        </select>
      </div>

      <h3 className="cv-subhead">Remit-To</h3>
      <span className="cv-field-hint cv-field-hint--block">
        Printed at the top of every estimate and invoice. Without it the customer has no address
        to mail a check to.
      </span>

      <div className="cv-field">
        <label className="cv-label">Street Address</label>
        <input
          className="tt-input"
          value={draft.business.addressLine1 ?? ''}
          onChange={e => updateBusiness('addressLine1', e.target.value)}
          placeholder="1234 Industrial Pkwy"
          maxLength={80}
        />
      </div>

      <div className="cv-field">
        <label className="cv-label">Suite / Unit</label>
        <input
          className="tt-input"
          value={draft.business.addressLine2 ?? ''}
          onChange={e => updateBusiness('addressLine2', e.target.value)}
          placeholder="Bay 3"
          maxLength={80}
        />
      </div>

      <div className="cv-row">
        <div className="cv-field">
          <label className="cv-label">City</label>
          <input
            className="tt-input"
            value={draft.business.city ?? ''}
            onChange={e => updateBusiness('city', e.target.value)}
            maxLength={60}
          />
        </div>
        <div className="cv-field">
          <label className="cv-label">State</label>
          <input
            className="tt-input"
            value={draft.business.state ?? ''}
            onChange={e => updateBusiness('state', e.target.value)}
            maxLength={2}
          />
        </div>
        <div className="cv-field">
          <label className="cv-label">ZIP</label>
          <input
            className="tt-input"
            value={draft.business.postalCode ?? ''}
            onChange={e => updateBusiness('postalCode', e.target.value)}
            maxLength={10}
          />
        </div>
      </div>

      <div className="cv-field">
        <label className="cv-label">Shop Phone</label>
        <input
          className="tt-input"
          value={draft.business.phone ?? ''}
          onChange={e => updateBusiness('phone', e.target.value)}
          placeholder="(555) 555-0134"
          maxLength={30}
        />
      </div>

      <div className="cv-field">
        <label className="cv-label">Billing Email</label>
        <input
          className="tt-input"
          type="email"
          value={draft.business.email ?? ''}
          onChange={e => updateBusiness('email', e.target.value)}
          placeholder="billing@yourshop.com"
          maxLength={80}
        />
      </div>

      <div className="cv-field">
        <label className="cv-label">Website</label>
        <input
          className="tt-input"
          value={draft.business.website ?? ''}
          onChange={e => updateBusiness('website', e.target.value)}
          placeholder="yourshop.com"
          maxLength={80}
        />
      </div>
    </>
  );
}

// ─── Branding Tab ─────────────────────────────────────────────────────────────

function BrandingTab({ draft, updateBranding, fileRef, handleLogoUpload, handleLogoRemove, logoUploading, logoError }) {
  const logoSrc = draft.branding.logoUrl || draft.branding.logoBase64 || rekonLogo;
  const isCustomLogo = !!(draft.branding.logoKey || draft.branding.logoBase64);

  return (
    <>
      <div className="cv-field">
        <label className="cv-label">App Name</label>
        <input
          className="tt-input"
          value={draft.branding.appName}
          onChange={e => updateBranding('appName', e.target.value)}
          placeholder="REKON"
          maxLength={40}
        />
        <span className="cv-field-hint">Shown in the header and browser tab</span>
      </div>

      <div className="cv-field">
        <label className="cv-label">Logo</label>
        <div className="cv-logo-row">
          <div className="cv-logo-thumb">
            <img src={logoSrc} alt="Logo preview" className="cv-logo-img" />
          </div>
          <div className="cv-logo-controls">
            <input
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              ref={fileRef}
              style={{ display: 'none' }}
              onChange={handleLogoUpload}
            />
            <button className="tt-button" onClick={() => fileRef.current?.click()} disabled={logoUploading}>
              {logoUploading ? 'Uploading...' : isCustomLogo ? 'Replace Logo' : 'Upload Logo'}
            </button>
            {isCustomLogo && (
              <button
                className="tt-button tt-button--ghost"
                onClick={handleLogoRemove}
                disabled={logoUploading}
              >
                Remove
              </button>
            )}
            <span className="cv-upload-hint">PNG, SVG, WebP, or JPEG · max 1 MB · stored in S3</span>
            {logoError && <span className="cv-upload-error">{logoError}</span>}
          </div>
        </div>
      </div>

      <div className="cv-field">
        <label className="cv-label">Colors</label>
        <div className="cv-color-list">
          {COLOR_FIELDS.map(({ key, label, hint }) => (
            <div key={key} className="cv-color-row">
              <input
                type="color"
                className="cv-swatch"
                value={draft.branding[key]}
                onChange={e => updateBranding(key, e.target.value)}
                title={label}
              />
              <div className="cv-color-meta">
                <span className="cv-color-name">{label}</span>
                <span className="cv-color-hint">{hint}</span>
              </div>
              <input
                className="cv-hex-input"
                value={draft.branding[key]}
                onChange={e => {
                  const v = e.target.value;
                  if (/^#[0-9a-fA-F]{0,6}$/.test(v)) updateBranding(key, v);
                }}
                spellCheck={false}
                maxLength={7}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="cv-field">
        <label className="cv-label">Preview</label>
        <div className="cv-preview-bar" style={{ background: draft.branding.primaryColor }}>
          <img src={logoSrc} alt="" className="cv-preview-logo" />
          <span className="cv-preview-name">{draft.branding.appName}</span>
          <div className="cv-preview-chips">
            <span className="cv-chip" style={{ background: draft.branding.accentColor, color: '#000' }}>Active</span>
            <span className="cv-chip" style={{ background: draft.branding.successColor, color: '#fff' }}>Healthy</span>
            <span className="cv-chip" style={{ background: draft.branding.dangerColor, color: '#fff' }}>Critical</span>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Flow Tab ─────────────────────────────────────────────────────────────────

function FlowTab({ draft, updateFlow }) {
  const cols = draft.flow.columns;
  const dragIdx = useRef(null);
  const [overIdx, setOverIdx] = useState(null);

  function reorder(from, to) {
    if (from === to || to == null) return;
    const next = [...cols];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    updateFlow(next.map((c, i) => ({ ...c, order: i })));
  }

  function toggleVisible(idx) {
    updateFlow(cols.map((c, i) => i === idx ? { ...c, visible: !c.visible } : c));
  }

  function updateLabel(idx, label) {
    updateFlow(cols.map((c, i) => i === idx ? { ...c, label } : c));
  }

  const visibleCount = cols.filter(c => c.visible).length;

  return (
    <>
      <p className="cv-flow-hint">
        Drag rows to reorder · toggle the eye to show or hide a column · rename any label.
        Hiding a column keeps jobs in the system — they just won't show in Flow.
      </p>

      <div className="cv-col-list">
        {cols.map((col, idx) => (
          <div
            key={col.id}
            className={[
              'cv-col-row',
              !col.visible            ? 'cv-col-row--hidden'  : '',
              overIdx === idx         ? 'cv-col-row--dragover' : '',
              dragIdx.current === idx ? 'cv-col-row--dragging' : '',
            ].filter(Boolean).join(' ')}
            draggable
            onDragStart={(e) => {
              dragIdx.current = idx;
              e.dataTransfer.effectAllowed = 'move';
            }}
            onDragEnd={() => {
              reorder(dragIdx.current, overIdx);
              dragIdx.current = null;
              setOverIdx(null);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              if (overIdx !== idx) setOverIdx(idx);
            }}
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget)) setOverIdx(null);
            }}
          >
            <span className="cv-col-handle" title="Drag to reorder">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                <rect x="2" y="2" width="4" height="2" rx="1"/>
                <rect x="8" y="2" width="4" height="2" rx="1"/>
                <rect x="2" y="6" width="4" height="2" rx="1"/>
                <rect x="8" y="6" width="4" height="2" rx="1"/>
                <rect x="2" y="10" width="4" height="2" rx="1"/>
                <rect x="8" y="10" width="4" height="2" rx="1"/>
              </svg>
            </span>

            <input
              className="cv-col-label"
              value={col.label}
              onChange={e => updateLabel(idx, e.target.value)}
              placeholder={col.id}
              disabled={!col.visible}
            />

            <span className="cv-col-id">{col.id}</span>

            <button
              className={`cv-col-toggle ${col.visible ? 'cv-col-toggle--on' : 'cv-col-toggle--off'}`}
              onClick={() => toggleVisible(idx)}
              title={col.visible ? 'Hide column' : 'Show column'}
              disabled={col.visible && visibleCount <= 1}
            >
              {col.visible ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
              )}
            </button>
          </div>
        ))}
      </div>

      <p className="cv-flow-footer-note">
        {visibleCount} of {cols.length} columns visible
      </p>
    </>
  );
}

// ─── Archive Tab ──────────────────────────────────────────────────────────────

function ArchiveTab({ draft, updateArchive }) {
  const archive = draft.archive || DEFAULT_SHOP_SETTINGS.archive;

  return (
    <>
      <div className="cv-field">
        <label className="cv-check-row">
          <input
            type="checkbox"
            checked={archive.keepFullRecordsForever}
            onChange={e => updateArchive('keepFullRecordsForever', e.target.checked)}
          />
          <span>
            <strong>Keep full service records forever</strong>
            <small>Completed jobs remain searchable with notes, status history, and job details.</small>
          </span>
        </label>
      </div>

      <div className="cv-field">
        <label className="cv-check-row">
          <input
            type="checkbox"
            checked={archive.keepPhotosForever}
            onChange={e => updateArchive('keepPhotosForever', e.target.checked)}
          />
          <span>
            <strong>Keep photos forever</strong>
            <small>S3 photo attachments stay available from History and service records.</small>
          </span>
        </label>
      </div>

      <div className="cv-field">
        <label className="cv-label">Summarize Records After</label>
        <select
          className="tt-input"
          value={archive.summarizeAfterMonths}
          onChange={e => updateArchive('summarizeAfterMonths', Number(e.target.value))}
        >
          <option value={12}>12 months</option>
          <option value={24}>24 months</option>
          <option value={36}>36 months</option>
          <option value={60}>5 years</option>
        </select>
        <span className="cv-field-hint">Summary fields can speed up reporting later without removing original records.</span>
      </div>

      <div className="cv-field">
        <label className="cv-check-row cv-check-row--danger">
          <input
            type="checkbox"
            checked={archive.allowAutomaticDeletion}
            onChange={e => updateArchive('allowAutomaticDeletion', e.target.checked)}
          />
          <span>
            <strong>Allow automatic deletion</strong>
            <small>Leave this off unless the business has a written retention policy.</small>
          </span>
        </label>
      </div>
    </>
  );
}

// ─── CustomizeView ────────────────────────────────────────────────────────────

export default function CustomizeView({ onBack }) {
  const { settings, save, reset } = useShopSettings();
  const [activeTab, setActiveTab] = useState('branding');
  const [draft, setDraft] = useState(null);
  const [draftDirty, setDraftDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState('');
  const fileRef = useRef(null);

  // Only re-initialize draft from settings if user hasn't made unsaved changes.
  // This prevents catalog auto-saves from clobbering an unsaved branding/flow draft.
  useEffect(() => {
    if (draftDirty) return;
    setDraft({
      ...settings,
      business: { ...settings.business },
      branding: { ...settings.branding },
      flow: {
        ...settings.flow,
        columns: settings.flow.columns.map(c => ({ ...c })),
      },
      archive: { ...(settings.archive || DEFAULT_SHOP_SETTINGS.archive) },
      pricing: { ...(settings.pricing || DEFAULT_SHOP_SETTINGS.pricing) },
    });
  }, [settings, draftDirty]);

  if (!draft) return null;

  function updateBranding(key, value) {
    setDraftDirty(true);
    setDraft(prev => ({ ...prev, branding: { ...prev.branding, [key]: value } }));
  }

  function updateBusiness(key, value) {
    setDraftDirty(true);
    setDraft(prev => ({ ...prev, business: { ...prev.business, [key]: value } }));
  }

  function updateFlow(columns) {
    setDraftDirty(true);
    setDraft(prev => ({ ...prev, flow: { ...prev.flow, columns } }));
  }

  function updatePricing(key, value) {
    setDraftDirty(true);
    setDraft(prev => ({
      ...prev,
      pricing: { ...(prev.pricing || DEFAULT_SHOP_SETTINGS.pricing), [key]: value },
    }));
  }

  // `group` is 'items' (QuickBooks Product/Service names) or 'accounts'
  // (Desktop chart-of-accounts names); omit it to set a top-level key.
  function updateAccounting(key, value, group = null) {
    setDraftDirty(true);
    setDraft(prev => {
      const acc = { ...DEFAULT_SHOP_SETTINGS.accounting, ...(prev.accounting || {}) };
      if (!group) return { ...prev, accounting: { ...acc, [key]: value } };
      const base = { ...DEFAULT_SHOP_SETTINGS.accounting[group], ...(acc[group] || {}) };
      return { ...prev, accounting: { ...acc, [group]: { ...base, [key]: value } } };
    });
  }

  function updateArchive(key, value) {
    setDraftDirty(true);
    setDraft(prev => ({
      ...prev,
      archive: {
        ...(prev.archive || DEFAULT_SHOP_SETTINGS.archive),
        [key]: value,
      },
    }));
  }

  function handleLogoRemove() {
    updateBranding('logoKey', null);
    updateBranding('logoUrl', null);
    updateBranding('logoBase64', null);
  }

  async function handleLogoUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setLogoError('');
    if (file.size > 1_048_576) {
      setLogoError('Logo must be under 1 MB. Try resizing or compressing the image.');
      e.target.value = '';
      return;
    }
    if (!/^image\/(png|jpeg|svg\+xml|webp)$/.test(file.type)) {
      setLogoError('Use PNG, SVG, WebP, or JPEG.');
      e.target.value = '';
      return;
    }

    setLogoUploading(true);
    try {
      const safeName = file.name.toLowerCase().replace(/[^a-z0-9.-]+/g, '-').replace(/^-+|-+$/g, '') || 'logo';
      const key = `shop-branding/${Date.now()}-${safeName}`;
      await uploadData({
        path: `public/${key}`,
        data: file,
        options: { contentType: file.type },
      }).result;

      updateBranding('logoKey', key);
      updateBranding('logoUrl', URL.createObjectURL(file));
      updateBranding('logoBase64', null);
    } catch (err) {
      console.error('Logo upload failed:', err);
      setLogoError('Upload failed. Check the connection and try again.');
    } finally {
      setLogoUploading(false);
      e.target.value = '';
    }
  }

  async function handleSave() {
    setSaving(true);
    // Preserve catalog — it auto-saves through CatalogTab, not this button
    await save({ ...draft, catalog: settings.catalog, pricing: { ...settings.pricing, ...draft.pricing } });
    setSaving(false);
    setSaved(true);
    setDraftDirty(false);
    setTimeout(() => setSaved(false), 2500);
  }

  async function handleReset() {
    if (!window.confirm('Reset all customizations to REKON defaults?')) return;
    await reset();
    setDraftDirty(false);
    setDraft({
      ...DEFAULT_SHOP_SETTINGS,
      business: { ...DEFAULT_SHOP_SETTINGS.business },
      branding: { ...DEFAULT_SHOP_SETTINGS.branding },
      flow: { columns: DEFAULT_SHOP_SETTINGS.flow.columns.map(c => ({ ...c })) },
      archive: { ...DEFAULT_SHOP_SETTINGS.archive },
    });
  }

  const TABS = [
    { id: 'business', label: 'Business Profile' },
    { id: 'billing',  label: 'Billing' },
    { id: 'branding', label: 'Branding' },
    { id: 'flow',     label: 'Flow Layout' },
    { id: 'archive',  label: 'Archive' },
    { id: 'catalog',  label: 'Service Catalog' },
    { id: 'manual',   label: 'User Manual' },
  ];

  const isCatalog = activeTab === 'catalog';
  // The manual is a document, not a settings form — it renders full width and
  // has nothing to save.
  const isManual = activeTab === 'manual';

  return (
    <div className="cv-page">
      <div className="cv-header">
        {onBack && (
          <button className="cv-back-btn" onClick={onBack}>← Back</button>
        )}
        <h1 className="cv-title">Customize</h1>
        <p className="cv-subtitle">Tailor REKON to your shop</p>
      </div>

      <div className={`cv-body ${isCatalog || isManual ? 'cv-body--wide' : ''}`}>
        <div className={`cv-panel ${isCatalog ? 'cv-panel--catalog' : ''}`}>
          {/* Tab bar */}
          <div className="cv-tabs">
            {TABS.map(tab => (
              <button
                key={tab.id}
                className={`cv-tab ${activeTab === tab.id ? 'cv-tab--active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {isManual ? (
            <div className="cv-tab-body cv-tab-body--manual">
              <ManualView />
            </div>
          ) : isCatalog ? (
            <div className="cv-tab-body cv-tab-body--catalog">
              <CatalogTab />
            </div>
          ) : (
            <>
              <div className="cv-tab-body">
                {activeTab === 'business' && (
                  <BusinessTab draft={draft} updateBusiness={updateBusiness} />
                )}
                {activeTab === 'billing' && (
                  <BillingTab draft={draft} updatePricing={updatePricing} updateAccounting={updateAccounting} />
                )}
                {activeTab === 'branding' && (
                  <BrandingTab
                    draft={draft}
                    updateBranding={updateBranding}
                    fileRef={fileRef}
                    handleLogoUpload={handleLogoUpload}
                    handleLogoRemove={handleLogoRemove}
                    logoUploading={logoUploading}
                    logoError={logoError}
                  />
                )}
                {activeTab === 'flow' && (
                  <FlowTab draft={draft} updateFlow={updateFlow} />
                )}
                {activeTab === 'archive' && (
                  <ArchiveTab draft={draft} updateArchive={updateArchive} />
                )}
              </div>

              {/* Footer Actions — not shown for catalog (auto-saves) */}
              <div className="cv-actions">
                <button className="tt-button tt-button--ghost" onClick={handleReset}>
                  Reset to Default
                </button>
                <button
                  className="tt-button tt-button--primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Changes'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
